import type { SpinCoordinatorSnapshot, SpinPhase } from "../../spin/SpinCoordinatorTypes";
import { RunnerTimer } from "./RunnerTimer";
import type { PresentationTaskRunner } from "@slot/presentation";

export type HoldSource = "pointer" | "keyboard";

export interface HoldToSpinTuning {
  /** How long a press must last before it is a hold. */
  holdThresholdSeconds: number;
  /** The pause between a round ending and the next held spin. */
  nextSpinDelaySeconds: number;
}

export const DEFAULT_HOLD_TO_SPIN_TUNING: HoldToSpinTuning = { holdThresholdSeconds: 0.18, nextSpinDelaySeconds: 0.3 };

export interface HoldToSpinPolicyDeps {
  /** The chrome runner: real seconds, frozen by the dev pause. */
  runner: PresentationTaskRunner;
  /** Operator setting: hold-to-spin (turbo chaining) allowed. */
  enabled: boolean;
  tuning?: Partial<HoldToSpinTuning>;
  coordinator: { getSnapshot(): SpinCoordinatorSnapshot; subscribe(listener: () => void): () => void };
  /** False while autospin runs or an interaction block is up. */
  canUseInput(): boolean;
  actions: {
    /** A held spin (the cue, no haptic — the presenter decides). */
    spinHeld(): void;
    slam(): void;
    warmupStart(): void;
    warmupStop(): void;
    /** A hold engaged: the presenter hides the idle hint. */
    holdStarted(): void;
    /** A hold ended (release, or input became blocked): the presenter refreshes the idle hint. */
    holdStopped(): void;
  };
}

/**
 * The hold-to-spin rule. A press held past the threshold becomes a hold: a hold that engages while
 * a round is presenting slams it once and stops there; a hold that began in idle chains spins — the
 * next one `nextSpinDelaySeconds` after each round — until the first release from the source that
 * engaged it (a second finger's release on the button included; that release is never a tap) or
 * until input becomes blocked. One auto-slam per round, when its answer arrives. The warm-up visual
 * primes only a press that can lead somewhere (hold enabled, pressed from idle, input usable).
 */
export class HoldToSpinPolicy {
  private readonly tuning: HoldToSpinTuning;
  private readonly pressed: Record<HoldSource, boolean> = { pointer: false, keyboard: false };
  private holdTriggeredFrom: HoldSource | null = null;
  private holdActive = false;
  /**
   * The active hold started from idle. A hold engaged during presenting still auto-slams at the
   * threshold, but the next-spin schedule that drives the loop is suppressed: the player must be
   * idle for a loop to begin.
   */
  private holdInitiatedFromIdle = false;
  private autoSlamIssuedForCurrentRound = false;
  private lastObservedPhase: SpinPhase = "idle";
  private readonly holdTimer: RunnerTimer;
  private readonly nextSpinTimer: RunnerTimer;
  private unsubscribe?: () => void;

  public constructor(private readonly deps: HoldToSpinPolicyDeps) {
    this.tuning = { ...DEFAULT_HOLD_TO_SPIN_TUNING, ...(deps.tuning ?? {}) };
    this.holdTimer = new RunnerTimer(deps.runner, "spin_input");
    this.nextSpinTimer = new RunnerTimer(deps.runner, "spin_input");
  }

  public start(): void {
    this.unsubscribe = this.deps.coordinator.subscribe(() => this.onCoordinatorChange());
  }

  public stop(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.holdTimer.cancel();
    this.nextSpinTimer.cancel();
    this.pressed.pointer = false;
    this.pressed.keyboard = false;
    this.holdActive = false;
    this.holdTriggeredFrom = null;
    this.autoSlamIssuedForCurrentRound = false;
    this.holdInitiatedFromIdle = false;
    this.lastObservedPhase = "idle";
  }

  public isPressed(source: HoldSource): boolean {
    return this.pressed[source];
  }

  /** A hold is active or a press is down: the idle hint must not show. */
  public isEngaged(): boolean {
    return this.holdActive || this.pressed.pointer || this.pressed.keyboard;
  }

  /** A press began. `pressedFromIdle` is the coordinator's `canSpin` captured before any tap action ran. */
  public pressStart(source: HoldSource, pressedFromIdle: boolean): void {
    this.pressed[source] = true;
    // A press from the source that holds does not take the hold over: the first release from that
    // source ends it, no tap follows (a second finger's tap used to orphan a live chain, review
    // 2026-09-27 #8).
    this.holdTimer.cancel();

    // Hold disabled: tap-only flow, no warm-up, no timer.
    if (!this.deps.enabled) return;
    // No priming when a continued hold can't lead anywhere: the press began during presenting
    // (the loop won't engage), or input is blocked (a modal is up, autospin runs).
    if (!pressedFromIdle || !this.deps.canUseInput()) return;

    this.deps.actions.warmupStart();
    this.holdTimer.schedule(this.tuning.holdThresholdSeconds, () => {
      if (!this.pressed[source]) return;
      this.holdTriggeredFrom = source;
      this.startHold(pressedFromIdle);
    });
  }

  /**
   * A press ended. `wasPressed` is false for a stray release (a key-release fallback with no key
   * held, or a release whose press a block or `stop()` already cleared); `holdReleased` is true when
   * this release ended a hold — no tap follows it.
   */
  public pressEnd(source: HoldSource): { wasPressed: boolean; holdReleased: boolean } {
    const wasPressed = this.pressed[source];
    const holdReleased = this.holdTriggeredFrom === source;
    if (!wasPressed && !holdReleased) return { wasPressed: false, holdReleased: false };

    this.pressed[source] = false;
    this.holdTimer.cancel();
    if (holdReleased) {
      this.stopHold();
      this.holdTriggeredFrom = null;
      return { wasPressed, holdReleased: true };
    }
    this.deps.actions.warmupStop();
    return { wasPressed, holdReleased: false };
  }

  /** Autospin or an interaction block changed: a hold cannot continue while input is blocked. */
  public externalStateChanged(): void {
    if (!this.deps.canUseInput()) this.stopHold();
  }

  private startHold(initiatedFromIdle: boolean): void {
    if (this.holdActive) return;
    this.deps.actions.warmupStop();
    if (!this.deps.canUseInput()) return;

    this.deps.actions.holdStarted();
    this.holdActive = true;
    this.holdInitiatedFromIdle = initiatedFromIdle;
    this.autoSlamIssuedForCurrentRound = false;

    // A spin already presenting when the hold engages (the spacebar's key-down spin is mid-
    // presentation by the time the threshold elapses): slam it now so the loop can begin — the
    // coordinator listener only fires on future transitions.
    const snap = this.deps.coordinator.getSnapshot();
    if (snap.canSlamStop && !this.autoSlamIssuedForCurrentRound) {
      this.autoSlamIssuedForCurrentRound = true;
      this.deps.actions.slam();
      return;
    }
    // Only a hold that began in idle progresses the loop.
    if (!initiatedFromIdle) return;
    this.scheduleNextHeldSpin();
  }

  private stopHold(): void {
    this.nextSpinTimer.cancel();
    this.pressed.pointer = false;
    this.pressed.keyboard = false;
    this.holdActive = false;
    this.holdTriggeredFrom = null;
    this.autoSlamIssuedForCurrentRound = false;
    this.holdInitiatedFromIdle = false;
    this.deps.actions.warmupStop();
    this.deps.actions.holdStopped();
  }

  private onCoordinatorChange(): void {
    const snap = this.deps.coordinator.getSnapshot();
    // A round starts when the coordinator leaves idle, and its one auto-slam is still to come. Keyed
    // on the phase, not the round id: the id only arrives with the server's answer, and a stop is
    // taken while the request is still in flight (2026-09-26), so an id change re-armed the slam
    // for a round that had already been slammed.
    if (snap.phase !== "idle" && this.lastObservedPhase === "idle") this.autoSlamIssuedForCurrentRound = false;
    this.lastObservedPhase = snap.phase;
    if (!this.holdActive) return;
    if (!this.deps.canUseInput()) {
      this.stopHold();
      return;
    }
    // The round's one auto-slam waits for the answer. The coordinator announces "requesting" before
    // its pre-spin starts the reels, so a slam there found nothing to brake: from decision 20 (which
    // let a stop in while requesting) until 2026-09-26 every held round played its full spin, and the
    // reels still claimed the slam, so its cue played anyway.
    if (snap.phase === "presenting" && !this.autoSlamIssuedForCurrentRound) {
      this.autoSlamIssuedForCurrentRound = true;
      this.nextSpinTimer.cancel();
      this.deps.actions.slam();
      return;
    }
    if (snap.canSpin && this.holdInitiatedFromIdle) this.scheduleNextHeldSpin();
  }

  private scheduleNextHeldSpin(): void {
    if (!this.holdActive || this.nextSpinTimer.isPending) return;
    this.nextSpinTimer.schedule(this.tuning.nextSpinDelaySeconds, () => {
      if (!this.holdActive) return;
      if (!this.deps.canUseInput()) {
        this.stopHold();
        return;
      }
      if (!this.deps.coordinator.getSnapshot().canSpin) return;
      this.deps.actions.spinHeld();
    });
  }
}
